import { useEffect, useRef, useState } from "react";

const DURATION = 1200;

type CountUpStatProps = {
  to: number;
  suffix?: string;
  /** Homepage stats are "2.5×" and "5.0"; whole-number stats pass 0. */
  decimals?: number;
  className?: string;
};

/**
 * Counts from zero to `to` over 1200ms with a cubic ease-out — the reference's
 * exact curve, 1 - (1 - p)^3.
 *
 * Two production changes over the reference:
 *
 * 1. It starts when the number scrolls into view, not on mount. The reference
 *    fires on mount, but this section sits well below the fold, so the whole
 *    animation would finish unseen.
 * 2. Reduced motion skips the animation entirely and leaves the final value.
 *
 * The final value is the initial render state, so the served HTML contains
 * "2.5×" and "5.0" rather than zeros.
 */
export function CountUpStat({
  to,
  suffix = "",
  decimals = 1,
  className,
}: CountUpStatProps) {
  const format = (n: number) => n.toFixed(decimals) + suffix;
  const [display, setDisplay] = useState(() => format(to));
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();

        const start = performance.now();
        const step = (now: number) => {
          const p = Math.min(1, (now - start) / DURATION);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(format(to * eased));
          if (p < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
      },
      { threshold: 0.4 },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, suffix, decimals]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
